import type { IDataSource } from "@/data/dao/service/BaseServiceDao";
import { Format } from "@/data/util/Format";
import { Misc } from "@/data/util/Misc";

import { i18n } from "@/portal/Trans";
import type { ITableFilterExtension } from "@/portal/elements/DataTable";
import { type IOptionNameFormatter, Select } from "@/portal/formControls/Select";
import { TastierGlobal } from "@/portal/globalAccess";

interface ITableFilterDropdown<T> {
    property: string;
    dataSource?: IDataSource<T>;
    select: Select<T>;
}

export class TableFilter implements ITableFilterExtension {
    public onChange?: { (value: object) };

    private $element: JQuery;
    private $row: JQuery;
    private $input: JQuery;
    private dropdowns = new Array<ITableFilterDropdown<any>>();

    constructor(size?: string) {
        this.$element = $('<form class="kt-form kt-form--label-right kt-margin-t-5" />');
        this.$row = $(
            '<div class="row align-items-center"><div class="col-xl-8"><div class="row align-items-center" /></div></div>'
        )
            .appendTo(this.$element)
            .find(".align-items-center");

        //add search filter
        console.log(i18n("general.searchTerm"));
        const btnGroup = $(
            '<div class="' +
                (size === undefined ? "col-md-4" : size) +
                ' kt-margin-b-20-tablet-and-mobile"><div class="input-group kt-input-icon kt-input-icon--left"><input type="text" class="form-control" placeholder="' +
                i18n("general.searchTerm") +
                '"><span class="kt-input-icon__icon kt-input-icon__icon--left"><span style="z-index:4;"><i class="la la-search"></i></span></span><div class="input-group-append"><button class="btn btn-secondary" type="button">' +
                i18n("general.search") +
                "</button></div></div></div>"
        ).appendTo(this.$row);

        this.$input = btnGroup.find("input.form-control");
        btnGroup.find(".btn").click((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.$element.submit();
        });

        this.$element.submit((e) => {
            e.preventDefault();
            e.stopPropagation();

            if (this.onChange !== undefined) this.onChange(this.getValue());

            return false;
        });
    }

    public getValue(): object {
        const val = this.$input.val();

        const filter = {
            term: val === "" ? undefined : val,
        };

        for (const i in this.dropdowns) {
            const dp = this.dropdowns[i];
            const dpVal = dp.select.getValue();
            if (dpVal !== undefined) filter[dp.property] = dpVal;
        }

        return filter;
    }

    public setValue(d: object) {
        this.$input.val(d["term"] !== undefined ? d["term"] : "");

        for (const i in this.dropdowns) {
            const dp = this.dropdowns[i];

            const va = d[dp.property];
            if (va !== undefined) {
                if (Array.isArray(va)) dp.select.setValues(va);
                else dp.select.setValue(va);
            }
        }
    }

    public addDropdownFilter<T>(
        property: string,
        dataSource: IDataSource<T>,
        locationDependend?: boolean,
        showAllText?: string,
        initialized?: { () },
        formatter?: IOptionNameFormatter<T>,
        size?: string
    ) {
        const element = $('<select class="form-control bootstrap-select" />');
        const col = $(
            '<div class="' +
                (size !== undefined ? size : "col-md-4") +
                ' kt-margin-b-20-tablet-and-mobile"><div class="kt-form__group"><div class="kt-form__control"></div></div>'
        ).appendTo(this.$row);
        element.appendTo(col.find(".kt-form__control"));

        const select = new Select(element, dataSource, formatter, true, showAllText);
        this.dropdowns.push({
            select: select,
            property: property,
        });

        const fetchAndInit = () => {
            select.fetchData(() => {
                select.onChange = () => {
                    if (this.onChange !== undefined) this.onChange(this.getValue());
                };

                if (initialized !== undefined) initialized();
            });
        };

        if (locationDependend !== undefined && locationDependend === true) {
            if (TastierGlobal.getLocationSwitch()!.getCurrentLocationId() !== undefined) fetchAndInit();
        } else fetchAndInit();
    }

    public addDropdownFilterFromEnum(
        property: string,
        enu: any,
        transNameSpace: string,
        showAllText?: string,
        defaultValue?: any,
        multiSelect?: boolean,
        size?: string
    ) {
        const element = $('<select class="form-control bootstrap-select" />');
        const col = $(
            '<div class="' +
                (size !== undefined ? size : "col-md-4") +
                ' kt-margin-b-20-tablet-and-mobile"><div class="kt-form__group"><div class="kt-form__control"></div></div>'
        ).appendTo(this.$row);
        element.appendTo(col.find(".kt-form__control"));

        Misc.eachEnum(enu, (i, e) => {
            const isDefaultValue = multiSelect === true ? false : defaultValue !== undefined && defaultValue === i;
            element.append(
                '<option value="' +
                    i +
                    '" ' +
                    (isDefaultValue ? 'selected="selected"' : "") +
                    ">" +
                    i18n(transNameSpace + "." + e) +
                    "</option>"
            );
        });

        const select = new Select(element, undefined, undefined, false, showAllText, property, multiSelect);
        if (multiSelect === true && defaultValue !== undefined) select.setValues(defaultValue);

        select.onChange = () => {
            if (this.onChange !== undefined) this.onChange(this.getValue());
        };

        this.dropdowns.push({
            select: select,
            property: property,
        });
    }

    public addDropdownFilterFromNumericOptions(
        property: string,
        options: Array<{ value: number; label: string }>,
        showAllText?: string,
        defaultValue?: any,
        multiSelect?: boolean,
        size?: string
    ) {
        const element = $('<select class="form-control bootstrap-select" />');
        const col = $(
            '<div class="' +
                (size !== undefined ? size : "col-md-4") +
                ' kt-margin-b-20-tablet-and-mobile"><div class="kt-form__group"><div class="kt-form__control"></div></div>'
        ).appendTo(this.$row);
        element.appendTo(col.find(".kt-form__control"));

        options.forEach((i) => {
            const isDefaultValue =
                multiSelect === true ? false : defaultValue !== undefined && defaultValue === i.value;
            element.append(
                '<option value="' +
                    i.value +
                    '" ' +
                    (isDefaultValue ? 'selected="selected"' : "") +
                    ">" +
                    i.label +
                    "</option>"
            );
        });

        const select = new Select(element, undefined, undefined, false, showAllText, property, multiSelect);
        if (multiSelect === true && defaultValue !== undefined) select.setValues(defaultValue);

        select.onChange = () => {
            if (this.onChange !== undefined) this.onChange(this.getValue());
        };

        this.dropdowns.push({
            select: select,
            property: property,
        });
    }

    public addDropdownFilterFromOptions(
        property: string,
        options: Array<{ value: string; label: string }>,
        showAllText?: string,
        defaultVal?: string
    ) {
        const element = $('<select class="form-control bootstrap-select" />');
        const col = $(
            '<div class="col-md-4 kt-margin-b-20-tablet-and-mobile"><div class="kt-form__group"><div class="kt-form__control"></div></div>'
        ).appendTo(this.$row);
        element.appendTo(col.find(".kt-form__control"));

        for (const i in options)
            element.append(
                '<option value="' + options[i].value + '">' + Format.htmlEscape(options[i].label) + "</option>"
            );

        const select = new Select(element, undefined, undefined, false, showAllText);
        if (defaultVal !== undefined) select.setValue(defaultVal);

        this.dropdowns.push({
            select: select,
            property: property,
        });

        //bugfix because select2 is buggy
        if (defaultVal !== undefined) {
            window.setTimeout(() => {
                select.setValue(defaultVal);
                select.onChange = () => {
                    if (this.onChange !== undefined) this.onChange(this.getValue());
                };
            }, 2);
        } else {
            select.onChange = () => {
                if (this.onChange !== undefined) this.onChange(this.getValue());
            };
        }
    }

    public async refreshDropdownFilter(property: string) {
        const f = this.dropdowns.filter((c) => c.property === property)[0];
        if (f === undefined) return;

        f.select.onChange = undefined;
        f.select.setValue(undefined);
        await f.select.fetchData();

        f.select.onChange = () => {
            if (this.onChange) {
                this.onChange(this.getValue());
            }
        };
    }

    public addAction(name: string, handler: { () }, icon?: string) {
        let parent = this.$element.find("> .row > .col-xl-4 > .row");
        if (parent.length === 0)
            parent = $('<div class="col-xl-4"><div class="row pull-right" /></div>')
                .appendTo(this.$element.find("> .row"))
                .find("> .row");

        $(
            '<a href="#" class="btn btn-clean btn-icon-sm">' +
                (icon !== undefined ? '<i class="' + icon + '"></i> ' : "") +
                name +
                "</a>"
        )
            .appendTo(parent)
            .click((e) => {
                e.preventDefault();

                handler();
            });
    }

    public addLinkAction(name: string, link: string, icon?: string) {
        let parent = this.$element.find("> .row > .col-xl-4 > .row");
        if (parent.length === 0)
            parent = $('<div class="col-xl-4"><div class="row pull-right" /></div>')
                .appendTo(this.$element.find("> .row"))
                .find("> .row");

        $(
            '<a href="' +
                link +
                '" target="_blank" class="btn btn-clean btn-icon-sm">' +
                (icon !== undefined ? '<i class="' + icon + '"></i> ' : "") +
                name +
                "</a>"
        ).appendTo(parent);
    }

    public reset() {
        this.$input.val("");
    }

    public appendTo(el: JQuery): TableFilter {
        el.append(this.$element);

        return this;
    }
}
